import React, { useRef, useState } from 'react';
import Button from '@amzn/awsui-components-console/button';
import Input from '@amzn/awsui-components-console/input';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import StatusIndicator from '@amzn/awsui-components-console/status-indicator';
import Box from '@amzn/awsui-components-console/box';
import './TestPage.scss';

const TestPage: React.FC = () => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [currentMode, setCurrentMode] = useState<'edit' | 'view'>('view');
  const [hasContent, setHasContent] = useState(false);
  const [artifactId, setArtifactId] = useState<string>('53456286-ee49-4978-b234-acbb9d1935e5');
  const [iframeSrc, setIframeSrc] = useState<string>('/html-editor');

  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const message = event.data;

      switch (message.type) {
        case 'MODE_CHANGED':
          setCurrentMode(message.payload?.isEditMode ? 'edit' : 'view');
          console.log('Mode changed to:', message.payload?.isEditMode ? 'edit' : 'view');
          break;
        case 'CONTENT_CHANGED':
          console.log('Content updated');
          setHasContent(!!message.payload?.html);
          break;
        case 'CONTENT_RESPONSE':
          console.log('Current content:', message.payload?.html);
          alert('Content retrieved! Check console for details.');
          break;
        case 'DOWNLOAD_INITIATED':
          console.log('Download initiated');
          break;
        case 'HTML_LOADED':
          console.log('HTML loaded successfully');
          setHasContent(true);
          break;
        case 'CONTENT_CLEARED':
          console.log('Content cleared successfully');
          setHasContent(false);
          break;
        case 'ARTIFACT_LOADED':
          console.log('Artifact loaded:', message.payload?.artifactId);
          setHasContent(true);
          break;
        case 'ARTIFACT_LOAD_ERROR':
          console.error('Failed to load artifact:', message.payload?.error);
          break;
        case 'SAVE_SUCCESS':
          console.log('Artifact saved successfully:', message.payload?.artifactId);
          break;
        case 'SAVE_ERROR':
          console.error('Failed to save artifact:', message.payload?.error);
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const sendMessage = (message: { type: string; payload?: unknown; messageId?: string }) => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(message, '*');
    }
  };

  const toggleMode = () => {
    const newMode = currentMode === 'edit' ? 'view' : 'edit';
    sendMessage({
      type: 'SET_MODE',
      payload: { isEditMode: newMode === 'edit' },
      messageId: Date.now().toString(),
    });
  };

  const downloadFile = () => {
    sendMessage({ type: 'DOWNLOAD', messageId: Date.now().toString() });
  };

  const loadSampleHTML = async () => {
    try {
      const response = await fetch('/src/components/HTMLEditor/templates/template.html');
      const htmlContent = await response.text();
      sendMessage({
        type: 'LOAD_HTML',
        payload: { html: htmlContent },
        messageId: Date.now().toString(),
      });
    } catch (error) {
      console.error('Failed to load template:', error);
      alert('Failed to load template. Check console for details.');
    }
  };

  const clearContent = () => {
    sendMessage({
      type: 'CLEAR_CONTENT',
      messageId: Date.now().toString(),
    });
  };

  const loadArtifact = () => {
    if (artifactId.trim()) {
      setIframeSrc(`/artifacts/${artifactId.trim()}`);
      // Reset hasContent since we're loading new content
      setHasContent(false);
    }
  };

  const loadEmptyEditor = () => {
    setIframeSrc('/html-editor');
    setArtifactId('');
    setHasContent(false);
  };

  const saveContent = () => {
    sendMessage({
      type: 'SAVE_CONTENT',
      messageId: Date.now().toString(),
    });
  };

  return (
    <div className="test-page">
      <Box padding={{ vertical: 'xl', horizontal: 'l' }}>
        <SpaceBetween size="l">
          <Header
            variant="h1"
            description="This page demonstrates the embeddable HTML editor with cross-origin communication"
            actions={
              <Button iconName="external" onClick={() => window.open('/editor', '_blank')}>
                Open Editor
              </Button>
            }
          >
            HTML Editor Test Page
          </Header>

          <div className="test-layout">
            <div>
              <Container header={<Header variant="h2">Editor Controls</Header>}>
                <SpaceBetween size="m">
                  <div>
                    <Box variant="h3" padding={{ bottom: 'xs' }}>
                      Status
                    </Box>
                    <SpaceBetween size="xs">
                      <div>
                        <Box variant="awsui-key-label">Current Mode</Box>
                        <StatusIndicator type={currentMode === 'edit' ? 'info' : 'success'}>{currentMode}</StatusIndicator>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Content</Box>
                        <StatusIndicator type={hasContent ? 'success' : 'stopped'}>{hasContent ? 'Loaded' : 'Empty'}</StatusIndicator>
                      </div>
                    </SpaceBetween>
                  </div>

                  <div>
                    <Box variant="h3" padding={{ bottom: 'xs' }}>
                      Mode Control
                    </Box>
                    <Button onClick={toggleMode} disabled={!hasContent}>
                      Toggle Mode
                    </Button>
                  </div>

                  <div>
                    <Box variant="h3" padding={{ bottom: 'xs' }}>
                      Artifact Loading
                    </Box>
                    <SpaceBetween size="xs">
                      <Input
                        type="text"
                        placeholder="Enter artifact ID"
                        value={artifactId}
                        onChange={(event) => setArtifactId(event.detail.value)}
                      />
                      <SpaceBetween size="xs" direction="horizontal">
                        <Button onClick={loadArtifact} disabled={!artifactId.trim()}>
                          Load Artifact
                        </Button>
                        <Button onClick={loadEmptyEditor}>Load Empty Editor</Button>
                      </SpaceBetween>
                    </SpaceBetween>
                  </div>

                  <div>
                    <Box variant="h3" padding={{ bottom: 'xs' }}>
                      Content Actions
                    </Box>
                    <SpaceBetween size="xs">
                      <SpaceBetween size="xs" direction="horizontal">
                        <Button onClick={loadSampleHTML}>Load Template</Button>
                        <Button onClick={clearContent} disabled={!hasContent}>
                          Clear Content
                        </Button>
                      </SpaceBetween>
                      <SpaceBetween size="xs" direction="horizontal">
                        <Button onClick={downloadFile} disabled={!hasContent}>
                          Download HTML
                        </Button>
                        <Button variant="primary" onClick={saveContent} disabled={!hasContent || !artifactId}>
                          Save Content
                        </Button>
                      </SpaceBetween>
                    </SpaceBetween>
                  </div>

                  <div>
                    <Box variant="h3" padding={{ bottom: 'xs' }}>
                      Embed Code
                    </Box>
                    <Box variant="code">
                      <pre className="embed-code-pre">
                        {`<iframe
  src="${window.location.origin}/artifacts/{artifactsId}"
  width="100%"
  height="600"
></iframe>`}
                      </pre>
                    </Box>
                  </div>
                </SpaceBetween>
              </Container>
            </div>

            <div>
              <Container header={<Header variant="h2">Embedded Editor</Header>}>
                <div className="iframe-container">
                  <iframe ref={iframeRef} src={iframeSrc} width="100%" height="600" title="HTML Editor" className="editor-iframe" />
                </div>
              </Container>
            </div>
          </div>
        </SpaceBetween>
      </Box>
    </div>
  );
};

export default TestPage;
